import {
  SUMMARY_ENTITY_BIOSTIMULANT,
  SUMMARY_ENTITY_OPERATION,
  SUMMARY_ENTITY_PLANT_PROTECTION,
  SUMMARY_ENTITY_PRODUCTION,
  SUMMARY_ENTITY_SEED,
  SUMMARY_ENTITY_SEED_DRESS,
  SUMMARY_ENTITY_SOIL_INPUT,
} from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput, IUnit } from 'interfaces/IInput';
import {
  type IInputStep,
  type IInputStepProduction,
  type IInputStepRateCost,
  type IInputStepSeed,
  InputStepType,
} from 'interfaces/IInputStep';
import type { IInputTask, InputTaskCategory } from 'interfaces/IInputTask';
import {
  COST_STEP_TYPES,
  STEP_TYPE_TO_QUANTITY_CATEGORY,
} from 'pages/FarmsPage/Workspace/Insights/costCategories';
import {
  convertToHectares,
  convertToKg,
  convertToLitres,
  resolveUnitKind,
  type UnitKind,
} from 'utils/unitConversion';

import type { SummaryEntityCategory } from '../../../../../redux/slices/InsightsSlice';
import { resolveAreaHa, resolveEffectiveRate } from '../insightUtils';

import {
  pairRowsBySide,
  type SideBySideRow,
  SUMMARY_DATA_SIDE_CATEGORIES,
  type SummaryDataSide,
} from './dataBasis';
import { type ResolvedCrop, resolveInputCrop } from './summary2Utils';

/* ═══════════════════════════════════════════════════════════════
   Types
   ═══════════════════════════════════════════════════════════════ */

export interface SummaryReportRow {
  key: string;
  label: string;
  /** Crop name — only set on Seed section rows. */
  group?: string;
  /** Number of steps merged into this row. */
  mergeCount?: number;
  /** null when nothing contributed a quantity to this row. */
  quantity: number | null;
  /** Base unit the quantity is expressed in ('kg' or 'L') — resolved to a nicer display unit (e.g. 't') at render time. */
  quantityUnit?: string;
  /** null when nothing contributed a cost/income to this row. $ cost, or $ income for the Production section. */
  cost: number | null;
  /** Area-weighted cost ÷ area (Σcost ÷ Σarea across the row's cost-contributing steps). null when the row has no area to divide by. */
  costPerHa: number | null;
}

export interface SummaryReportSection {
  id: SummaryEntityCategory;
  title: string;
  rows: SummaryReportRow[];
  /** True only for Seed — rows are grouped under a Crop column, labelled by Variety. */
  hasGroup: boolean;
  /** "Cost ($)" normally, "Income ($)" for Production. */
  costLabel: string;
  /** "Avg. $/ha" normally, "Avg. Income/ha" for Production — "Avg." because a row can blend several steps/paddocks at different rates. */
  costPerHaLabel: string;
  /** True for the six physical-input categories — these get a per-section Monthly breakdown expander. */
  showMonthlyToggle: boolean;
}

/** One report row per side (Plan / Actual), matched by row key — each side is its own row, never summed. */
export interface SummaryReportSideBySideRow
  extends SideBySideRow<SummaryReportRow> {
  label: string;
  group?: string;
}

export interface SummaryReportSideBySideSection
  extends Omit<SummaryReportSection, 'rows'> {
  rows: SummaryReportSideBySideRow[];
}

/* ═══════════════════════════════════════════════════════════════
   Section metadata, in report display order
   ═══════════════════════════════════════════════════════════════ */

const SECTION_ORDER: SummaryEntityCategory[] = [
  SUMMARY_ENTITY_SEED,
  SUMMARY_ENTITY_SOIL_INPUT,
  SUMMARY_ENTITY_PLANT_PROTECTION,
  SUMMARY_ENTITY_SEED_DRESS,
  SUMMARY_ENTITY_BIOSTIMULANT,
  SUMMARY_ENTITY_OPERATION,
  SUMMARY_ENTITY_PRODUCTION,
];

const SECTION_TITLES: Partial<Record<SummaryEntityCategory, string>> = {
  [SUMMARY_ENTITY_SEED]: 'Seed Summary',
  [SUMMARY_ENTITY_SOIL_INPUT]: 'Fertiliser Summary',
  [SUMMARY_ENTITY_PLANT_PROTECTION]: 'Chemical Summary',
  [SUMMARY_ENTITY_SEED_DRESS]: 'Seed Treatment Summary',
  [SUMMARY_ENTITY_BIOSTIMULANT]: 'Biostimulant Summary',
  [SUMMARY_ENTITY_OPERATION]: 'Operations Summary',
  [SUMMARY_ENTITY_PRODUCTION]: 'Production Summary',
};

const MONTHLY_TOGGLE_CATEGORIES = new Set<SummaryEntityCategory>([
  SUMMARY_ENTITY_SEED,
  SUMMARY_ENTITY_SOIL_INPUT,
  SUMMARY_ENTITY_PLANT_PROTECTION,
  SUMMARY_ENTITY_SEED_DRESS,
  SUMMARY_ENTITY_BIOSTIMULANT,
  SUMMARY_ENTITY_OPERATION,
]);

/* ═══════════════════════════════════════════════════════════════
   Per-step helpers — mirror computeSummary.ts's private versions
   ═══════════════════════════════════════════════════════════════ */

const isValidGlobalId = (id: string | null | undefined): id is string =>
  typeof id === 'string' && id.length > 0;

const stepKey = (step: IInputStep): string | null => {
  if (isValidGlobalId(step.globalId)) return step.globalId;
  return step.item || null;
};

const stepCategory = (step: IInputStep): SummaryEntityCategory | null =>
  step.stepType === InputStepType.Production
    ? SUMMARY_ENTITY_PRODUCTION
    : (STEP_TYPE_TO_QUANTITY_CATEGORY[step.stepType] ?? null);

/** Quantity in the step's own recorded unit — Seed uses rate×area, cost steps use effectiveRate×area, Production uses yield×area. */
const calculateQuantityWithUnit = (
  category: SummaryEntityCategory,
  step: IInputStep,
  task: IInputTask,
  areaHa: number,
  gUnits: GUnit[]
): { value: number; unit: IUnit } | null => {
  if (category === SUMMARY_ENTITY_SEED) {
    const s = step as unknown as IInputStepRateCost;
    const rate = s.rate ?? 0;
    if (rate <= 0 || areaHa <= 0 || !s.appUnit) return null;
    return { value: rate * areaHa, unit: s.appUnit };
  }

  if (COST_STEP_TYPES.has(step.stepType)) {
    const s = step as unknown as IInputStepRateCost;
    const effectiveRate = resolveEffectiveRate(step, task, gUnits);
    if (
      effectiveRate === null ||
      effectiveRate <= 0 ||
      areaHa <= 0 ||
      !s.appUnit
    )
      return null;
    return { value: effectiveRate * areaHa, unit: s.appUnit };
  }

  if (step.stepType === InputStepType.Production) {
    const s = step as IInputStepProduction;
    const raw = (s.yield ?? 0) * areaHa;
    if (raw <= 0 || !s.yieldUnit) return null;
    return { value: raw, unit: s.yieldUnit };
  }

  return null;
};

/** Cost = rate × cost × area for cost steps; Production returns revenue = yield × price × area. */
const calculateCost = (
  step: IInputStep,
  task: IInputTask,
  areaHa: number,
  gUnits: GUnit[]
): number => {
  if (step.stepType === InputStepType.Production) {
    const s = step as IInputStepProduction;
    const yld = s.yield ?? 0;
    const price = s.price ?? 0;
    if (yld <= 0 || price <= 0 || areaHa <= 0) return 0;
    return yld * price * areaHa;
  }

  if (!COST_STEP_TYPES.has(step.stepType)) return 0;
  const s = step as unknown as IInputStepRateCost;
  const cost = s.cost ?? 0;
  const effectiveRate = resolveEffectiveRate(step, task, gUnits);
  if (effectiveRate === null || effectiveRate <= 0 || cost <= 0 || areaHa <= 0)
    return 0;
  return effectiveRate * cost * areaHa;
};

/* ═══════════════════════════════════════════════════════════════
   Accumulator — one per section
   ═══════════════════════════════════════════════════════════════ */

interface SectionAccumulator {
  order: string[];
  /** Tracks which keys have already been added to `order` — independent of `label`, which callers may set before or after `touchRow` runs. */
  seen: Set<string>;
  label: Map<string, string>;
  group: Map<string, string>;
  quantity: Map<string, number>;
  quantityKind: Map<string, UnitKind>;
  cost: Map<string, number>;
  /** Area behind the row's cost contributions only (Σarea across cost-contributing steps) — used to derive costPerHa. */
  costArea: Map<string, number>;
  mergeCount: Map<string, number>;
}

const newAccumulator = (): SectionAccumulator => ({
  order: [],
  seen: new Set(),
  label: new Map(),
  group: new Map(),
  quantity: new Map(),
  quantityKind: new Map(),
  cost: new Map(),
  costArea: new Map(),
  mergeCount: new Map(),
});

const touchRow = (acc: SectionAccumulator, key: string): void => {
  if (acc.seen.has(key)) return;
  acc.seen.add(key);
  acc.order.push(key);
};

/** Seed rows sort by Crop then Variety; every other section by label. */
const compareRows = (
  a: { label: string; group?: string },
  b: { label: string; group?: string }
): number => {
  if (a.group && b.group && a.group !== b.group) {
    return a.group.localeCompare(b.group, undefined, {
      sensitivity: 'base',
    });
  }
  return a.label.localeCompare(b.label, undefined, {
    sensitivity: 'base',
  });
};

/* ═══════════════════════════════════════════════════════════════
   Public API
   ═══════════════════════════════════════════════════════════════ */

/**
 * Input summaries sections. Reads only the tasks in `categories` — callers
 * pass one category (Plan or Actual) so the same item recorded in both is
 * never added together.
 */
export const computeSummaryReport = (
  inputs: IInput[],
  gUnits: GUnit[],
  categories: Set<InputTaskCategory>
): SummaryReportSection[] => {
  const accumulators = new Map<SummaryEntityCategory, SectionAccumulator>();
  const getAcc = (category: SummaryEntityCategory): SectionAccumulator => {
    let acc = accumulators.get(category);
    if (!acc) {
      acc = newAccumulator();
      accumulators.set(category, acc);
    }
    return acc;
  };

  const resolvedCrops = new Map<string, ResolvedCrop>();
  for (const input of inputs) {
    resolvedCrops.set(input.id, resolveInputCrop(input));
  }

  for (const input of inputs) {
    const resolved = resolvedCrops.get(input.id)!;

    for (const task of input.tasks) {
      if (!categories.has(task.category)) continue;

      for (const step of task.steps) {
        const category = stepCategory(step);
        if (!category) continue;

        const areaHa = resolveAreaHa(step, task, input, gUnits);
        const acc = getAcc(category);

        let key: string;
        if (category === SUMMARY_ENTITY_SEED) {
          const s = step as unknown as IInputStepSeed;
          // Variety identifies which seed product this row is (a per-step
          // detail); Crop always comes from the document-level attribute
          // (resolveInputCrop), never the step's own item — same rule Area
          // Summary, Cost by Crop, and Plan vs Actual all follow.
          key = s.item2 ?? resolved.variety ?? '';
          if (!acc.group.has(key)) {
            acc.group.set(key, resolved.crop);
          }
          acc.label.set(key, key);
        } else {
          const k = stepKey(step);
          if (k === null) continue;
          key = k;
          if (!acc.label.has(key) && step.item) acc.label.set(key, step.item);
        }

        touchRow(acc, key);

        let contributed = false;

        const quantity = calculateQuantityWithUnit(
          category,
          step,
          task,
          areaHa,
          gUnits
        );
        if (quantity) {
          const kind = resolveUnitKind(quantity.unit, gUnits);
          if (kind) {
            const converted =
              kind === 'mass'
                ? convertToKg(quantity.value, quantity.unit, gUnits)
                : convertToLitres(quantity.value, quantity.unit, gUnits);
            if (converted !== null) {
              const established = acc.quantityKind.get(key);
              if (!established || established === kind) {
                acc.quantityKind.set(key, kind);
                acc.quantity.set(key, (acc.quantity.get(key) ?? 0) + converted);
                contributed = true;
              }
              // Row already settled on the other kind — can't merge without
              // a bulk-density assumption, so this contribution is skipped.
            }
          }
        }

        const cost = calculateCost(step, task, areaHa, gUnits);
        if (cost !== 0) {
          acc.cost.set(key, (acc.cost.get(key) ?? 0) + cost);
          if (areaHa > 0) {
            acc.costArea.set(key, (acc.costArea.get(key) ?? 0) + areaHa);
          }
          contributed = true;
        }

        if (contributed) {
          acc.mergeCount.set(key, (acc.mergeCount.get(key) ?? 0) + 1);
        }
      }
    }
  }

  /* ── Soil-input bulk-density rows recorded as liquid still converge to kg
     alongside mass rows of the same product — computeSummary.ts's ALL-mode
     quantity path handles this via convertToTonnes with bulkDensity; this
     lens's per-row Quantity column instead keeps each row in its own
     recorded kind (mass or liquid), matching the per-category behaviour
     computeSummary.ts itself uses outside ALL mode. ── */

  const sections: SummaryReportSection[] = [];
  for (const category of SECTION_ORDER) {
    const acc = accumulators.get(category);
    if (!acc || acc.order.length === 0) continue;

    const rows: SummaryReportRow[] = acc.order
      .map((key) => {
        const costArea = acc.costArea.get(key) ?? 0;
        const cost = acc.cost.has(key) ? acc.cost.get(key)! : null;
        const row: SummaryReportRow = {
          key,
          label: acc.label.get(key) ?? key,
          quantity: acc.quantity.has(key) ? acc.quantity.get(key)! : null,
          cost,
          costPerHa: cost !== null && costArea > 0 ? cost / costArea : null,
        };
        const group = acc.group.get(key);
        if (group !== undefined) row.group = group;
        const mergeCount = acc.mergeCount.get(key);
        if (mergeCount !== undefined && mergeCount > 1)
          row.mergeCount = mergeCount;
        const kind = acc.quantityKind.get(key);
        if (kind) row.quantityUnit = kind === 'mass' ? 'kg' : 'L';
        return row;
      })
      .sort(compareRows);

    const isProduction = category === SUMMARY_ENTITY_PRODUCTION;
    sections.push({
      id: category,
      title: SECTION_TITLES[category] ?? category,
      rows,
      hasGroup: category === SUMMARY_ENTITY_SEED,
      costLabel: isProduction ? 'Income ($)' : 'Cost ($)',
      costPerHaLabel: isProduction ? 'Avg. Income/ha' : 'Avg. $/ha',
      showMonthlyToggle: MONTHLY_TOGGLE_CATEGORIES.has(category),
    });
  }

  return sections;
};

/**
 * Input summaries for each requested side, with sections and rows matched
 * across sides — one side for Plan or Actual, both for Plan vs Actual
 * (paired columns, never summed). A section appears when any side has it.
 */
export const computeSummaryReportBySide = (
  inputs: IInput[],
  gUnits: GUnit[],
  sides: SummaryDataSide[]
): SummaryReportSideBySideSection[] => {
  const sectionsBySide = new Map<SummaryDataSide, SummaryReportSection[]>();
  for (const side of sides) {
    sectionsBySide.set(
      side,
      computeSummaryReport(inputs, gUnits, SUMMARY_DATA_SIDE_CATEGORIES[side])
    );
  }

  const result: SummaryReportSideBySideSection[] = [];
  for (const category of SECTION_ORDER) {
    const rowsBySide: Partial<Record<SummaryDataSide, SummaryReportRow[]>> = {};
    let meta: SummaryReportSection | undefined;
    for (const side of sides) {
      const section = sectionsBySide
        .get(side)
        ?.find((candidate) => candidate.id === category);
      if (!section) continue;
      if (!meta) meta = section;
      rowsBySide[side] = section.rows;
    }
    if (!meta) continue;

    const rows = pairRowsBySide(rowsBySide, (row) => row.key)
      .map((paired) => {
        const first = Object.values(paired.sides)[0];
        return {
          ...paired,
          label: first?.label ?? paired.key,
          group: first?.group,
        };
      })
      .sort(compareRows);

    result.push({ ...meta, rows });
  }

  return result;
};

/* ═══════════════════════════════════════════════════════════════
   Area Summary — crop → area (ha), current tree scope only
   ═══════════════════════════════════════════════════════════════ */

export interface AreaSummaryRow {
  crop: string;
  areaHa: number;
}

export const computeAreaSummary = (
  inputs: IInput[],
  gUnits: GUnit[]
): AreaSummaryRow[] => {
  const totals = new Map<string, number>();

  for (const input of inputs) {
    const zoneArea = input.zone?.zoneArea ?? 0;
    if (zoneArea <= 0) continue;
    const areaHa =
      convertToHectares(zoneArea, input.zone?.areaUnit ?? null, gUnits) ??
      zoneArea;
    if (areaHa <= 0) continue;

    const { crop } = resolveInputCrop(input);
    const label = crop || 'Unspecified Crop';
    totals.set(label, (totals.get(label) ?? 0) + areaHa);
  }

  return Array.from(totals.entries())
    .map(([crop, areaHa]) => ({ crop, areaHa }))
    .sort((a, b) =>
      a.crop.localeCompare(b.crop, undefined, { sensitivity: 'base' })
    );
};
